import React, { useMemo, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FiTrash2 } from "react-icons/fi";
import { useTranslation } from "react-i18next";

import MainButton from "../../components/button/MainButton";
import ConfirmModal from "../../components/modal/ConfirmModal";
import IconButton from "../../components/button/IconButton";

import UserService from "../../services/userService";
import { getDateString } from "../../utils/formatters";
import { getErrorToastMessage } from "../../utils/httpToast";

export default function UserDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userId } = useParams();
  const location = useLocation();
  const { state } = location;

  const returnUrl = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    const raw = sp.get("returnUrl");
    return raw ? decodeURIComponent(raw) : "/users";
  }, [location.search]);

  const userService = UserService();

  const [user, setUser] = useState(state?.user ?? null);
  const [loading, setLoading] = useState(!state?.user);

  const [role, setRole] = useState(user?.role ?? "USER");
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const goUsersWithToast = (type, message) => {
    navigate(returnUrl, { state: { toast: { type, message } } });
  };

  useEffect(() => {
    if (user) return;

    const controller = new AbortController();

    const fetchDetail = async () => {
      try {
        setLoading(true);
        const u = await userService.userDetail(userId, { signal: controller.signal });

        const mapped = {
          id: u.id,
          email: u.email,
          createdAt: u.createdDate ? getDateString(u.createdDate, false) : "-",
          role: u.role,
        };

        setUser(mapped);
        setRole(mapped.role ?? "USER");
      } catch (e) {
        if (e.name === 'CanceledError') return;
        goUsersWithToast("error", getErrorToastMessage(e, t));
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
    return () => controller.abort();
  }, [user, userId]);

  if (loading) {
    return (
      <div className="w-full bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-6 py-14">
          <p className="text-sm text-gray-600">{t("ACTIONS.LOADING_MORE")}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="w-full bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-6 py-14">
          <h1 className="text-2xl font-semibold text-gray-900 mb-6">
            {t("HEADER.USER")} #{userId}
          </h1>

          <div className="rounded-2xl border border-gray-200 p-6">
            <p className="text-sm text-gray-600">{t("USERS.NOT_FOUND")}</p>

            <div className="mt-5">
              <MainButton variant="secondary" onClick={() => navigate(returnUrl)}>
                {t("USERS.RETURN")}
              </MainButton>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const onCancel = () => navigate(returnUrl);

  const onSave = async () => {
    try {
      await userService.updateUserRole({ id: user.id, role });
      goUsersWithToast("success", t("MODAL.SUCCESSFUL"));
    } catch (err) {
      goUsersWithToast("error", getErrorToastMessage(err, t));
    }
  };

  const onDelete = () => setConfirmDeleteOpen(true);

  const confirmDelete = async () => {
    try {
      await userService.deleteUser(user.id);
      goUsersWithToast("success", t("MODAL.SUCCESSFUL"));
    } catch (err) {
      goUsersWithToast("error", getErrorToastMessage(err, t));
    } finally {
      setConfirmDeleteOpen(false);
    }
  };

  return (
    <div className="w-full bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-6 py-14">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">
            {t("USERS.EDIT")}
          </h1>
        </div>

        <div className="relative rounded-2xl border border-gray-200 p-6">
          <IconButton
            className="absolute top-4 right-4 sm:hidden"
            aria-label={t("USERS.DELETE")}
            onClick={onDelete}
            title={t("USERS.DELETE")}
          >
            <FiTrash2 className="text-lg text-red" />
          </IconButton>

          <div className="grid grid-cols-1 gap-6 sm:[grid-template-columns:repeat(auto-fit,minmax(60px,1fr))]">
            <div className="min-w-0">
              <p className="text-xs text-gray-500 mb-1">{t("FORM_FIELDS.EMAIL")}</p>
              <div className="h-11 flex items-center text-sm font-medium text-gray-900 break-words">
                {user.email}
              </div>
            </div>

            <div className="min-w-0">
              <p className="text-xs text-gray-500 mb-1">{t("USERS.CREATION_DATE")}</p>
              <div className="h-11 flex items-center text-sm text-gray-900">
                {user.createdAt}
              </div>
            </div>

            <div className="min-w-0">
              <p className="text-xs text-gray-500 mb-1">{t("USERS.ROLE")}</p>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="h-11 w-fit min-w-[180px] max-w-[240px] rounded-lg border border-gray-300 px-3 text-sm outline-none"
              >
                <option value="USER">USER</option>
                <option value="ADMIN">ADMIN</option>
              </select>
            </div>
          </div>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <IconButton
              className="hidden sm:inline-flex"
              aria-label={t("ACTIONS.DELETE")}
              onClick={onDelete}
              title={t("USERS.DELETE")}
            >
              <FiTrash2 className="text-lg text-red" />
            </IconButton>

            <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
              <MainButton
                variant="secondary"
                className="w-full sm:w-auto px-6 py-3 text-base"
                onClick={onCancel}
              >
                {t("ACTIONS.CANCEL")}
              </MainButton>

              <MainButton
                variant="primary"
                className="w-full sm:w-auto px-6 py-3 text-base"
                onClick={onSave}
              >
                {t("ACTIONS.BUTTON_SAVE")}
              </MainButton>
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={confirmDelete}
        title={t("MODAL.REMOVE")}
        description={t("MODAL.SAFE_REMOVE_USER")}
        cancelLabel={t("ACTIONS.CANCEL")}
        confirmLabel={t("MODAL.REMOVE")}
        confirmClassName="danger"
      />
    </div>
  );
}